import {
  Avatar,
  Box,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  useTheme,
  type StepIconProps,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';

/**
 * Eventos que o cardápio dispara automaticamente para Meta Pixel + GA4 quando um
 * ID de tracking está configurado. A lista espelha, em ordem de funil, o que
 * `lib/tracking.ts` realmente envia hoje — mantê-las em sincronia ao adicionar/
 * remover eventos lá. É informativo (marketing planejar o funil), não configura
 * nada. `curto` é o rótulo do passo (cabe na horizontal); o nome completo + os
 * nomes técnicos ficam no tooltip.
 */
const EVENTOS_FUNIL: Array<{ curto: string; rotulo: string; meta: string; ga: string }> = [
  { curto: 'Acesso', rotulo: 'Acesso ao cardápio', meta: 'PageView', ga: 'view_item_list' },
  { curto: 'Ver produto', rotulo: 'Visualização de produto', meta: 'ViewContent', ga: 'view_item' },
  { curto: 'Carrinho', rotulo: 'Adição ao carrinho', meta: 'AddToCart', ga: 'add_to_cart' },
  { curto: 'Cadastro', rotulo: 'Cadastro / identificação', meta: 'CompleteRegistration', ga: 'sign_up' },
  { curto: 'Checkout', rotulo: 'Início do checkout', meta: 'InitiateCheckout', ga: 'begin_checkout' },
  { curto: 'Pagamento', rotulo: 'Forma de pagamento', meta: 'AddPaymentInfo', ga: 'add_payment_info' },
  { curto: 'Compra', rotulo: 'Compra concluída', meta: 'Purchase', ga: 'purchase' },
];

/** Passo do funil numerado, em cor de destaque (sem semântica de "concluído"). */
function IconePasso(props: StepIconProps): JSX.Element {
  return (
    <Avatar
      sx={{
        width: 26,
        height: 26,
        fontSize: 13,
        fontWeight: 700,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
      }}
    >
      {props.icon}
    </Avatar>
  );
}

/**
 * Cartão informativo (só leitura) que mostra, como um FUNIL horizontal, os
 * eventos que o cardápio registra no Meta Pixel e no Google Analytics. Cada passo
 * traz a etapa; o tooltip revela o nome técnico do evento em cada provedor —
 * ajuda o marketing a montar funis/conversões sem adivinhar os nomes. A ordem = a
 * jornada do cliente. Em telas estreitas, rola na horizontal (não esmaga os passos).
 */
export function EventosRastreados(): JSX.Element {
  const tema = useTheme();
  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 2, md: 2.5 },
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: tema.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'grey.50',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <InsightsIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">Eventos rastreados no funil</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Com um Pixel ou Google Analytics configurado acima, o cardápio registra automaticamente estas etapas da
        jornada do cliente — nos dois provedores. Passe o mouse (ou toque) em cada passo para ver o nome técnico.
      </Typography>

      {/* Scroll horizontal em telas estreitas: preserva a leitura de funil sem esmagar. */}
      <Box sx={{ overflowX: 'auto', pt: 1, mx: -0.5 }}>
        <Stepper alternativeLabel activeStep={-1} sx={{ minWidth: 560 }}>
          {EVENTOS_FUNIL.map((evento) => (
            <Step key={evento.rotulo} completed active={false}>
              <Tooltip title={`${evento.rotulo} — Meta Pixel: ${evento.meta} · GA4: ${evento.ga}`} arrow>
                <StepLabel StepIconComponent={IconePasso} sx={{ cursor: 'help' }}>
                  {evento.curto}
                </StepLabel>
              </Tooltip>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Box>
  );
}
